if (!customElements.get("product-gallery")) {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.galleryCore = window.IWCProductGalleryCore;
      this.desktopMedia = window.matchMedia("(min-width: 992px)");
      this.main = this.querySelector("[data-product-gallery-main]");
      this.mainContainer = this.main?.querySelector(".swiper-container");
      this.mainWrapper = this.mainContainer?.querySelector(".swiper-wrapper");
      this.thumbnails = this.querySelector("[data-product-gallery-thumbnails]");
      this.thumbnailContainer = this.thumbnails?.querySelector(".swiper-container");
      this.thumbnailWrapper = this.thumbnailContainer?.querySelector(".swiper-wrapper");
      this.pagination = this.querySelector(".product__gallery-pagination");
      this.fabricNotice = this.querySelector("[data-gallery-fabric-notice]");
      this.prev = this.querySelector(".product__gallery-nav--prev");
      this.next = this.querySelector(".product__gallery-nav--next");
      this.allMainSlides = Array.from(this.querySelectorAll("[data-gallery-main-slide]"));
      this.allThumbnailSlides = Array.from(this.querySelectorAll(".product__gallery-thumbnail-slide"));
      this.visibleThumbnailSlides = [];
      this.swiper = undefined;
      this.thumbnailSwiper = undefined;
      this.selectedFabric = this.dataset.selectedFabric || "";
      this.selectedFamily = this.dataset.selectedFamily || "";
      this.selectedOptionTokens = this.galleryCore.parseTokens(this.dataset.selectedOptionTokens);
      this.resolvedMediaIndexes = null;
      this._mediaChangeHandler = () => this.onResize();
      this._thumbnailClickHandler = (event) => this.onThumbnailClick(event);
      this._configurationHandler = (event) => {
        if (this.fabricNotice) {
          this.fabricNotice.hidden = event.detail.gallery?.fabricMismatch !== true;
          this.fabricNotice.textContent = event.detail.gallery?.materialMismatch
            ? this.fabricNotice.dataset.materialMessage : this.fabricNotice.dataset.fabricMessage;
        }
        this.selectedFabric = event.detail.fabricHandle || "";
        this.selectedFamily = event.detail.familyHandle || "";
        this.selectedOptionTokens = Array.isArray(event.detail.optionTokens)
          ? event.detail.optionTokens
          : this.galleryCore.parseTokens(event.detail.optionTokens);
        this.resolvedMediaIndexes = Array.isArray(event.detail.gallery?.mediaIndexes)
          ? event.detail.gallery.mediaIndexes.map((index) => String(index))
          : null;
        this.filterMedia();
      };

      this.desktopMedia.addEventListener("change", this._mediaChangeHandler);
      this.thumbnails?.addEventListener("click", this._thumbnailClickHandler);
      window.addEventListener("pdpConfigurationChange", this._configurationHandler);

      this.initMainSwiper();
      this.filterMedia();
      this.onResize();
      this.listen();
    }

    disconnectedCallback() {
      if (this._configurationHandler) {
        window.removeEventListener("pdpConfigurationChange", this._configurationHandler);
      }
      if (this._mediaChangeHandler) {
        this.desktopMedia.removeEventListener("change", this._mediaChangeHandler);
      }
      if (this._thumbnailClickHandler) {
        this.thumbnails?.removeEventListener("click", this._thumbnailClickHandler);
      }
      if (this._videoObserver) {
        this._videoObserver.disconnect();
      }
      if (this.thumbnailSwiper) {
        this.thumbnailSwiper.destroy(true, true);
      }
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }
    }

    get mainSlides() {
      return this.allMainSlides || Array.from(this.querySelectorAll("[data-gallery-main-slide]"));
    }

    get thumbnailSlides() {
      return this.allThumbnailSlides || Array.from(this.querySelectorAll(".product__gallery-thumbnail-slide"));
    }

    initMainSwiper() {
      if (this.swiper || !this.mainContainer) return;

      this.swiper = new Swiper(this.mainContainer, {
        loop: false,
        grabCursor: false,
        noSwipingSelector: ".swiper-no-swiping, model-viewer",
        speed: 500,
        spaceBetween: 0,
        slidesPerView: 1,
        watchSlidesProgress: true,
        navigation: {
          nextEl: this.next,
          prevEl: this.prev,
        },
        pagination: {
          el: this.pagination,
          clickable: true,
        },
        on: {
          slideChange: () => {
            this.updateThumbnailWindow();
            this.updateSwipers();
            this.syncActiveState();
            this.playActiveVideo();
          },
          slideChangeTransitionEnd: () => {
            this.swiper?.pagination?.render();
            this.swiper?.pagination?.update();
          },
        },
      });
    }

    initThumbnailSwiper() {
      if (this.thumbnailSwiper || !this.thumbnailContainer) return;

      this.thumbnailSwiper = this.galleryCore.createThumbnailSwiper(this.thumbnailContainer, {
        slidesPerView: 4,
        spaceBetween: 7,
        breakpoints: {
          992: {
            slidesPerView: 5,
            spaceBetween: 4,
          },
        },
        on: {
          slideChange: () => this.updateThumbnailWindow(),
        },
      });
    }

    destroyThumbnailSwiper() {
      if (!this.thumbnailSwiper) return;

      this.thumbnailSwiper.destroy(true, true);
      this.thumbnailSwiper = undefined;
    }

    filterMedia() {
      const result = this.galleryCore.filterPairedSlides({
        slides: this.mainSlides,
        thumbnailSlides: this.thumbnailSlides,
        mainWrapper: this.mainWrapper,
        thumbnailWrapper: this.thumbnailWrapper,
        selectedFabric: this.selectedFabric,
        selectedFamily: this.selectedFamily,
        selectedOptionTokens: this.selectedOptionTokens,
        resolvedMediaIndexes: this.resolvedMediaIndexes,
      });

      this.classList.toggle("product__gallery--single", result.visibleCount <= 1);
      this.visibleThumbnailSlides = result.visibleThumbnailSlides;
      this.updateThumbnailWindow();
      this.updateSwipers();
      this.slideToFirstVisible();
      this.syncActiveState();
      setTimeout(() => window.dispatchEvent(new Event("resize")));
    }

    updateSwipers() {
      this.swiper?.update();
      this.swiper?.navigation?.update();
      this.swiper?.pagination?.render();
      this.swiper?.pagination?.update();
      this.thumbnailSwiper?.update();
    }

    updateThumbnailWindow() {
      const visibleLimit = this.desktopMedia.matches ? 5 : 4;
      const lastWindowStart = Math.max(this.visibleThumbnailSlides.length - visibleLimit, 0);
      const windowStart = Math.min(this.thumbnailSwiper?.activeIndex || 0, lastWindowStart);
      const windowEnd = Math.min(windowStart + visibleLimit, this.visibleThumbnailSlides.length);
      const remainingCount = this.visibleThumbnailSlides.length - windowEnd;

      this.thumbnailOverflow?.remove();
      this.thumbnailOverflow = undefined;
      if (!remainingCount) return;

      const sourceSlide = this.visibleThumbnailSlides[windowEnd - 1];
      const sourceButton = sourceSlide?.querySelector("[data-gallery-thumbnail]");
      if (!sourceButton) return;

      const overflowButton = sourceButton.cloneNode(true);
      const activeMediaIndex = this.swiper?.slides[this.swiper.activeIndex]?.dataset.mediaIndex;
      overflowButton.classList.add("product__gallery-thumbnail-overflow");
      overflowButton.setAttribute("data-gallery-thumbnail-overflow", "");
      overflowButton.setAttribute("aria-current", sourceSlide.dataset.mediaIndex === activeMediaIndex ? "true" : "false");
      const counter = overflowButton.querySelector("[data-gallery-thumbnail-count]");
      if (counter) counter.textContent = `+${remainingCount}`;
      this.thumbnailContainer.append(overflowButton);
      this.thumbnailOverflow = overflowButton;
    }

    slideToFirstVisible() {
      if (!this.swiper) return;

      const activeSlide = this.swiper.slides[this.swiper.activeIndex];
      if (activeSlide && activeSlide.parentElement === this.mainWrapper && !activeSlide.hidden) return;

      this.swiper.slideTo(0, 0);
    }

    onThumbnailClick(event) {
      const button = event.target.closest("[data-gallery-thumbnail]");
      if (!button) return;

      const targetIndex = this.galleryCore.getSlideIndexForMedia(this.swiper, this.mainSlides, button.dataset.galleryThumbnail);
      if (targetIndex < 0) return;
      this.swiper.slideTo(targetIndex);
    }

    syncActiveState() {
      this.galleryCore.syncActiveThumbnail({
        swiper: this.swiper,
        thumbnailSwiper: this.thumbnailSwiper,
        thumbnailSlides: this.thumbnailSlides,
        buttonSelector: "[data-gallery-thumbnail]",
      });
    }

    hydrateVideo(video, shouldPlay = false) {
      if (!video || video.dataset.videoLoaded === "true") {
        if (shouldPlay) {
          video?.play().catch(() => {});
        }
        return;
      }

      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.videoLoaded = "true";
      video.load();
      if (shouldPlay || video.hasAttribute("autoplay")) {
        video.play().catch(() => {});
      }
    }

    pauseVideos() {
      this.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    }

    playActiveVideo() {
      this.pauseVideos();
      const video = this.querySelector(".product__gallery-main .swiper-slide-active video");
      if (video) {
        this.hydrateVideo(video, true);
      }
    }

    observeVideos() {
      if (this._videoObserver) {
        this._videoObserver.disconnect();
      }

      const videos = this.querySelectorAll("video[data-lazy-video]");
      if (!videos.length) return;

      if (!("IntersectionObserver" in window)) {
        videos.forEach((video) => this.hydrateVideo(video));
        return;
      }

      this._videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.hydrateVideo(entry.target);
            this._videoObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "400px 0px" },
      );

      videos.forEach((video) => this._videoObserver.observe(video));
    }

    listen() {
      setTimeout(() => {
        this.classList.remove("loading");
      }, 500);

      this.observeVideos();
    }

    onResize() {
      this.initThumbnailSwiper();

      this.updateThumbnailWindow();
      this.updateSwipers();
      this.syncActiveState();
    }
  }

  customElements.define("product-gallery", ProductGallery);
}
